import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'

import { CURP_PATTERN, GUARANTOR_RELATIONSHIPS, GuarantorContact, RFC_PF_PATTERN, RFC_PM_PATTERN, toGuarantorPayload } from '@data/constants/contact-types'
import { ApiCustomerService } from '../../data/services/api/api-customer.service'
import { extractApiErrors } from '@data/utils/api-error.util'

const upper = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()

/** El RFC tiene el largo de su tipo de persona y no repite el del titular ni el de otro obligado (plan v4.3 §6.2). */
function guarantorRfcValidator(personType: () => 'PF' | 'PM', taken: () => { rfc: string; message: string }[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rfc = upper(control.value)
    if (!rfc) return null
    const pattern = personType() === 'PM' ? RFC_PM_PATTERN : RFC_PF_PATTERN
    if (!pattern.test(rfc)) return { rfcFormat: true }
    const clash = taken().find((item) => item.rfc === rfc)
    return clash ? { rfcTaken: clash.message } : null
  }
}

/**
 * Alta y edición de un obligado solidario desde el detalle del cliente (plan v4.3 §9.5). Guarda con
 * `PUT /clients/:id/guarantors`, así que no depende de que el formulario completo del cliente sea válido. Como la API
 * reemplaza el conjunto, manda a los demás obligados tal como están y solo cambia o agrega el que se captura aquí.
 */
@Component({
  selector: 'app-guarantor-contact-dialog',
  templateUrl: './guarantor-contact-dialog.component.html',
  styleUrls: ['./guarantor-contact-dialog.component.scss'],
})
export class GuarantorContactDialogComponent implements OnChanges {
  @Input() visible = false
  @Output() visibleChange = new EventEmitter<boolean>()
  @Input() clientId = ''
  @Input() holderRfc: string | null = null
  /** Los obligados que ya tiene el cliente, en el orden de la API. */
  @Input() guarantors: GuarantorContact[] = []
  /** El obligado que se edita; `null` da de alta uno nuevo. */
  @Input() editing: GuarantorContact | null = null
  @Output() saved = new EventEmitter<void>()

  readonly relationships = [...GUARANTOR_RELATIONSHIPS]
  readonly personTypes = [
    { label: 'Persona física', value: 'PF' },
    { label: 'Persona moral', value: 'PM' },
  ]

  form: FormGroup
  saving = false
  serverError = ''

  constructor(
    private api: ApiCustomerService,
    private fb: FormBuilder,
  ) {
    this.form = this.fb.group({
      personType: ['PF' as 'PF' | 'PM', Validators.required],
      relationship: [null as string | null, Validators.required],
      firstName: [''],
      lastName: [''],
      middleName: [''],
      businessName: [''],
      rfc: [
        '',
        [
          Validators.required,
          guarantorRfcValidator(
            () => this.personType,
            () => this.takenRfcs(),
          ),
        ],
      ],
      curp: ['', Validators.pattern(CURP_PATTERN)],
      phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      email: ['', Validators.email],
    })
    this.form.get('personType')!.valueChanges.subscribe(() => this.applyPersonType())
    this.applyPersonType()
  }

  get personType(): 'PF' | 'PM' {
    return this.form?.get('personType')?.value === 'PM' ? 'PM' : 'PF'
  }

  get title(): string {
    return this.editing ? 'Editar obligado solidario' : 'Agregar obligado solidario'
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) this.prefill()
  }

  private prefill(): void {
    const current = this.editing
    this.serverError = ''
    this.form.reset({
      personType: current?.personType === 'PM' ? 'PM' : 'PF',
      relationship: current?.relationship ?? null,
      firstName: current?.firstName ?? '',
      lastName: current?.lastName ?? '',
      middleName: current?.middleName ?? '',
      businessName: current?.businessName ?? '',
      rfc: current?.rfc ?? '',
      curp: current?.curp ?? '',
      phone: current?.phone ?? '',
      email: current?.email ?? '',
    })
  }

  //* PF exige nombre y apellido paterno; PM, razón social. Los campos de la otra persona no se validan ni se mandan.
  private applyPersonType(): void {
    const company = this.personType === 'PM'
    const required = (name: string, on: boolean) => {
      const control = this.form.get(name)!
      control.setValidators(on ? [Validators.required] : [])
      control.updateValueAndValidity({ emitEvent: false })
    }
    required('firstName', !company)
    required('lastName', !company)
    required('businessName', company)
    this.form.get('rfc')!.updateValueAndValidity({ emitEvent: false })
  }

  private takenRfcs(): { rfc: string; message: string }[] {
    const others = (this.guarantors ?? [])
      .filter((item) => item.id !== this.editing?.id)
      .map((item) => ({ rfc: upper(item.rfc), message: 'Ese RFC ya está en otro obligado solidario' }))
      .filter((item) => item.rfc)
    const holder = upper(this.holderRfc)
    return holder ? [...others, { rfc: holder, message: 'El obligado no puede tener el RFC del cliente' }] : others
  }

  /** El mensaje del campo, si ya se tocó y tiene error. */
  errorOf(name: string): string {
    const control = this.form.get(name)
    if (!control || !control.invalid || !(control.touched || control.dirty)) return ''
    const errors = control.errors ?? {}
    if (errors['required']) return REQUIRED_MESSAGES[name] ?? 'Este campo es obligatorio'
    if (errors['rfcTaken']) return errors['rfcTaken']
    if (errors['rfcFormat']) return this.personType === 'PM' ? 'El RFC de persona moral debe tener 12 caracteres' : 'El RFC de persona física debe tener 13 caracteres'
    if (name === 'phone') return 'El teléfono debe tener exactamente 10 dígitos'
    if (name === 'curp') return 'El CURP no tiene un formato válido'
    if (name === 'email') return 'El correo no tiene un formato válido'
    return 'Revisa este campo'
  }

  /** La lista completa que se manda: los demás tal cual y el capturado en su lugar (o al final si es nuevo). */
  buildPayload(): Record<string, string>[] {
    const captured = toGuarantorPayload({ ...this.form.getRawValue(), id: this.editing?.id })
    const rest = (this.guarantors ?? []).map((item) => (item.id === this.editing?.id ? captured : toGuarantorPayload(item)))
    return this.editing ? rest : [...rest, captured]
  }

  onVisibleChange(value: boolean): void {
    this.visible = value
    this.visibleChange.emit(value)
  }

  close(): void {
    this.onVisibleChange(false)
  }

  save(): void {
    this.form.get('rfc')!.setValue(upper(this.form.get('rfc')!.value), { emitEvent: false })
    this.form.get('curp')!.setValue(upper(this.form.get('curp')!.value), { emitEvent: false })
    this.form.updateValueAndValidity()
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }
    this.saving = true
    this.serverError = ''
    this.api.updateGuarantors(this.clientId, this.buildPayload()).subscribe((response) => {
      this.saving = false
      if (!response.error) {
        this.saved.emit()
        this.close()
        return
      }
      this.serverError = describeGuarantorError(response)
    })
  }
}

const REQUIRED_MESSAGES: Record<string, string> = {
  relationship: 'Elige la relación del obligado con el cliente',
  firstName: 'El nombre del obligado es obligatorio',
  lastName: 'El apellido paterno del obligado es obligatorio',
  businessName: 'La razón social del obligado es obligatoria',
  rfc: 'El RFC del obligado es obligatorio',
  phone: 'El teléfono del obligado es obligatorio',
}

/**
 * El error de la API en palabras. Una regla del conjunto trae `detail.index` (a qué obligado se refiere) y una
 * validación del DTO trae la ruta del campo («guarantors.0.El RFC…»), que se traduce a «Obligado 1: El RFC…».
 */
export function describeGuarantorError(response: { msg?: string; data?: any; status?: number; index?: number | null }): string {
  if (response.status === 0) return 'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.'
  if (response.status === 409) return response.msg || 'Los datos del cliente cambiaron mientras guardabas. Recarga la página e inténtalo de nuevo.'
  const messages = extractApiErrors(response, 'No se pudo guardar al obligado solidario. Inténtalo de nuevo.').map((text) =>
    text.replace(/^guarantors\.(\d+)\./, (_, index) => `Obligado ${Number(index) + 1}: `),
  )
  const text = messages.join('. ')
  return typeof response.index === 'number' ? `Obligado ${response.index + 1}: ${text}` : text
}
